import Immutable from "immutable"
import React, { useEffect, useMemo, useRef } from "react"
import { connect } from "react-redux"
import swal from "sweetalert"
import debounce from "lodash.debounce"

import { createSheetKey, mantineSheetConnection } from "app/static/frontend/sheets/selectors/sheet-selectors"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"
import { updateUserdata } from "shared/userdata/userdata-action-creators"

import ColumnActionMenu from "app/static/frontend/sheets/components/ColumnActionMenu"
import { MantineReactTableWrapper } from "app/static/frontend/sheets/components/MantineWrapper"
import TopToolbarCustomActions from "app/static/frontend/sheets/components/TopToolbarCustomActions"
import DatumCell from "app/static/frontend/sheets/components/cells/MantineCells/DatumCell"
import { ToolbarAlertBannerContent } from "app/static/frontend/sheets/components/ToolBarAlertBanner"

import sheetConstants from "app/static/frontend/sheets/constants"
import {
    datumAccessorForDataType,
    coerceValueToDataType,
} from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"
import { getColumnDefinition } from "app/static/frontend/sheets/mantine-sheet/column-definitions"
import {
    renameColumn,
    searchByColumnAtIndex,
    sortByColumnAtIndex,
    clearFilter,
    toggleDefaultSortOnColumn,
} from "app/static/frontend/sheets/sheet-action-creators"
import { cleanUpHtml } from "shared/imports/sharedHelperFunctions"

import swalConfigs from "app/static/frontend/swalConfigs"
import { swalContents } from "app/static/frontend/sheets/components/helpTexts"

import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { MantineAggregationCell } from "app/static/frontend/sheets/components/MantineAggregationCell"
import { Stack } from "@mantine/core"
import { IndexCell } from "app/static/frontend/sheets/components/cells/MantineCells/IndexCell"

// intermediate wrapper to "absorb" redux / immutableJS and convert it into cleaner callbacks for
// MantineReactTable.

export const LocalMantineSheet = (props) => {
    const {
        columns,
        columnsToRender,
        columnCount,
        rowCount,
        rowData,
        dataType,
        datumColumnWidth,
        resizeColumn,
        patchColumnSize,
        id,
        publicSheet,
        allowDatumLink,
        canAddColumns,
        canHideColumns,
        canRemoveColumns,
        canFitInMemory,
        isResizable,
        alignmentTypes,
        shouldDisplayMathBar,
        sheetId,
        saveColumnOrder,
        toggleDatumColumnIsHiddenOnPublic,
        toggleColumnIsHiddenOnPublic,
        showDatumColumn,
        renameColumn,
        removeColumn,
        canRenameColumns,
        shouldShowEmailAllButton,
        emailAllParams,
        createEmailFromSheet,
        sheetInitiallyLoading,
        isDownloading,
        download,
        isLoadingMathBar,
        toggleShowMath,
        params,
        isLoadingBigSheetData,
        stanceOptions,
        editSheet,
        searchByColumnAtIndex,
        sortByColumnAtIndex,
        loadNextInterval,
        clearFilter,
        canRemoveRows,
        removeRow,
        toggleDefaultSortOnColumn,
    } = props

    // Derive pinned columns directly from Redux store data instead of local state
    const pinnedColumns = useMemo(() => {
        const pinned: Record<string, boolean> = {}
        if (columns) {
            columns.forEach((column) => {
                // Use .get() method for Immutable.js objects
                const defaultSortType = column.get("default_sort_type")
                const columnId = column.get("id")

                if (defaultSortType && defaultSortType !== 0) {
                    pinned[columnId] = true
                }
            })
        }
        return pinned
    }, [columns])

    // Calculate initial sorting state based on pinned columns
    const initialSorting = useMemo(() => {
        if (!columns) return []

        const pinnedColumn = columns.find((column) => {
            const defaultSortType = column.get("default_sort_type")
            return defaultSortType && defaultSortType !== 0
        })

        if (pinnedColumn) {
            const columnId = String(pinnedColumn.get("id"))
            const defaultSortType = pinnedColumn.get("default_sort_type")
            // Ensure robust comparison by converting both to numbers
            const descendingValue = DjangIO.app.userdata.spreadsheet.types.SheetSortType.descending.value
            const isDescending = Number(defaultSortType) === Number(descendingValue)

            return [{ id: columnId, desc: isDescending }]
        }

        return []
    }, [columns])

    const togglePinnedSort = (columnId: string, tableInstance?: any) => {
        // Convert columnId to number for consistency with backend expectations
        const numericColumnId = Number(columnId)

        // Get current pinned state from Redux store data
        const isPinned = pinnedColumns[columnId]

        let sortType: number

        if (isPinned) {
            // If currently pinned, unpin it (set to 0)
            sortType = 0
        } else {
            // If not pinned, pin it with the current sort direction from the table
            sortType = DjangIO.app.userdata.spreadsheet.types.SheetSortType.ascending.value // default to ascending

            if (tableInstance) {
                const allSorting = tableInstance.getState().sorting
                const currentSort = allSorting.find((s: any) => s.id === columnId)

                // Check if there's a type mismatch
                if (!currentSort && allSorting.length > 0) {
                    // Try to find with string conversion
                    const currentSortByString = allSorting.find((s: any) => String(s.id) === String(columnId))
                    if (currentSortByString) {
                        // Use this match instead
                        sortType = currentSortByString.desc
                            ? DjangIO.app.userdata.spreadsheet.types.SheetSortType.descending.value
                            : DjangIO.app.userdata.spreadsheet.types.SheetSortType.ascending.value
                    }
                }

                if (currentSort) {
                    // Use the current sort direction from the table
                    sortType = currentSort.desc
                        ? DjangIO.app.userdata.spreadsheet.types.SheetSortType.descending.value
                        : DjangIO.app.userdata.spreadsheet.types.SheetSortType.ascending.value
                }
            }
        }

        // ENSURE ONLY ONE COLUMN CAN BE PINNED AT A TIME
        // If we're pinning a new column, unpin all other columns first
        if (!isPinned) {
            // Find currently pinned column and unpin it
            const currentlyPinnedColumnId = Object.keys(pinnedColumns).find((id) => pinnedColumns[id])
            if (currentlyPinnedColumnId && currentlyPinnedColumnId !== columnId) {
                // Unpin the currently pinned column in backend
                toggleDefaultSortOnColumn(id, Number(currentlyPinnedColumnId), 0)
            }
        }

        // Call backend to persist the change for the target column
        if (!publicSheet) {
            toggleDefaultSortOnColumn(id, numericColumnId, sortType)
        }
    }

    const columnsProcessed = useMemo(() => {
        const removeRowColumn = {
            id: sheetConstants.REMOVE_ROW_COLUMN_INDEX,
            columnDefType: "display",
            pinned: true, // our own property
            size: sheetConstants.INDEX_COLUMN_WIDTH,
            minSize: sheetConstants.INDEX_COLUMN_WIDTH,
            maxSize: sheetConstants.INDEX_COLUMN_WIDTH,
            Cell: ({ row }) => {
                const { id: rowId, index: rowIndex, ...rowProps } = row
                return (
                    <IndexCell
                        canRemoveRows={canRemoveRows}
                        id={id}
                        rowIndex={rowIndex}
                        rows={rowsProcessed}
                        isIndexCell={true}
                        {...rowProps}
                        {...props}
                    />
                )
            },
        }
        const datumColumn = {
            accessorFn: (rowModel) => cleanUpHtml(datumAccessorForDataType(dataType, rowModel)),
            header: DjangIO.app.models.QuorumDataType.by_value(dataType).singular,
            enableSorting: false,
            id: sheetConstants.DATUM_COLUMN_INDEX,
            pinned: true, // our own property
            size: datumColumnWidth || sheetConstants.DATUM_COLUMN_WIDTH,
            minSize: sheetConstants.MANTINE_COLUMN_MIN_WIDTH,
            maxSize: sheetConstants.COLUMN_MAX_WIDTH,
            enableColumnOrdering: false,
            enableColumnDragging: false,

            renderColumnActionsMenuItems: ({ column: columnForMenu, table }) => (
                <ColumnActionMenu
                    canAddColumns={canAddColumns}
                    canHideColumns={canHideColumns}
                    canRemoveColumns={false}
                    columnDefinition={columnForMenu}
                    isDatumColumn={true}
                    sheetId={sheetId}
                    columnIndex={sheetConstants.DATUM_COLUMN_INDEX}
                    // @ts-expect-error
                    columnId
                    toggleColumnIsHiddenOnPublic={() => toggleDatumColumnIsHiddenOnPublic(id, showDatumColumn)}
                    isHidden={!showDatumColumn}
                    canColorColumns={false}
                    table={table}
                />
            ),
            Cell: ({ column, row }) => (
                <DatumCell
                    dataType={dataType}
                    datum={Immutable.fromJS(row.original)}
                    style={{ padding: ".25rem .5rem" }}
                    // Maybe need to connect this to the `selectAllowDatumLink` selector
                    allowDatumLink={allowDatumLink}
                    // Should be connected to the redux store, but for now, just want to see tooltips
                    width={column.getSize()}
                />
            ),
            Header: ({ column }) => <div className="header-text">{column.columnDef.header}</div>,
            Footer:
                shouldDisplayMathBar &&
                (() => <MantineAggregationCell label={"Count:"} value={new Intl.NumberFormat().format(rowCount)} />),
        }
        const columnsToUse = columns?.toJS()
        if (!columnsToUse?.length) {
            return [removeRowColumn, datumColumn]
        }

        return [removeRowColumn, datumColumn].concat(
            columnsToUse.map((column, columnIndex) => {
                const removeMantineColumn = () => {
                    if (
                        DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(column.column_type)
                            .notify_on_remove
                    ) {
                        swal(swalConfigs.removeSheetColumnPrompt).then((willDelete) => {
                            if (willDelete) {
                                removeColumn(id, column.id, columnIndex)
                            }
                        })
                    } else {
                        removeColumn(id, column.id, columnIndex)
                    }
                }
                return getColumnDefinition({
                    alignmentTypes,
                    canAddColumns,
                    canFitInMemory,
                    canHideColumns,
                    canRemoveColumns,
                    column,
                    columns,
                    columnCount,
                    columnIndex,
                    removeColumn: removeMantineColumn,
                    columnsToRender,
                    dataType,
                    id,
                    rowData,
                    sheetId,
                    shouldDisplayMathBar,
                    stanceOptions,
                    toggleColumnIsHiddenOnPublic,
                    renameColumn,
                    canRenameColumns,
                    isPinned: pinnedColumns[column.id] || false,
                    togglePinnedSort,
                })
            }),
        )
    }, [
        pinnedColumns,
        dataType,
        shouldDisplayMathBar,
        rowCount,
        datumColumnWidth,
        columns,
        canAddColumns,
        canHideColumns,
        sheetId,
        showDatumColumn,
        toggleDatumColumnIsHiddenOnPublic,
        id,
        allowDatumLink,
        alignmentTypes,
        canFitInMemory,
        canRemoveColumns,
        columnCount,
        columnsToRender,
        rowData,
        toggleColumnIsHiddenOnPublic,
        renameColumn,
        canRenameColumns,
        removeColumn,
        stanceOptions,
        canRemoveRows,
        removeRow,
        togglePinnedSort,
    ])

    const rowsProcessed = useMemo(() => {
        return rowData.toJS().map((row) => ({
            ...row,
            safedSearchId: row.id,
        }))
    }, [rowData])

    // need a stable function definition for debounce
    const debouncedPatchColumnSize = useMemo(
        () => debounce((id, columnWidth, columnId) => patchColumnSize(id, columnWidth, columnId), 1000),
        [],
    )

    const handleColumnSizeChange = (columnId, columnWidth) => {
        // resize the column in the store
        resizeColumn(id, columnWidth, Number(columnId))

        // send a request to store the resized column size
        !publicSheet && debouncedPatchColumnSize(id, columnWidth, Number(columnId))
    }

    const handleColumnOrderChange = (payload) => {
        const columnIds = payload.slice(2).map((id) => Number(id))
        // send request to reorder in database
        // if called, we want to allow public sheets to reorder on the UI
        !publicSheet && saveColumnOrder(sheetId, columnIds)
    }

    const renderTopToolbarCustomActions = ({ table }) => (
        <Stack>
            <TopToolbarCustomActions
                shouldDisplayMathBar={shouldDisplayMathBar}
                isLoadingMathBar={isLoadingMathBar}
                toggleShowMath={toggleShowMath}
                params={params}
                canAddColumns={canAddColumns}
                sheetId={sheetId}
                columns={columns}
                sheetInitiallyLoading={sheetInitiallyLoading}
                isDownloading={isDownloading}
                download={download}
                shouldShowEmailAllButton={shouldShowEmailAllButton}
                createEmailFromSheet={createEmailFromSheet}
                emailAllParams={emailAllParams}
                table={table}
            />
            <ToolbarAlertBannerContent table={table} />
        </Stack>
    )

    const handleErr = async (error) => {
        editSheet(id, { initialSheetRequestLoading: false, loading: false })
        if (error.response.data && error.response.data.custom_error_message) {
            await swal({ ...swalContents.failLoadSheet, text: error.response.data.custom_error_message })
        } else if (error && error.response && error.response.status === 404) {
            await swal({ ...swalContents.failLoadSheet, text: "This sheet does not exist" })
        } else if (error && error.response && error.response.status === 524) {
            // This is a Cloudflare-specific Timeout status code, means the Sheet query is just too darn big
            Raven.captureMessage(
                `Sheets Query Timed Out on sheet ${id} for ${Userdata.organization_id} -  ${Userdata.organization}`,
            )
            // NOTE: runbook="https://quorumanalytics.atlassian.net/wiki/x/OgB4gg"
            const text =
                "Your current row selection or last column selection is too large to process. Please add filters to reduce the amount of data before loading the sheet or " +
                "reach out to us for assistance at support@quorum.us."
            await swal({ ...swalContents.failLoadSheet, text })
        } else {
            await BACKENDERROR(error, swalContents.failLoadSheet)
        }
    }

    // useRef allows us to have a reference to the promise for further safeguarding of the query
    const query = useRef(null)
    const tableContainerRef = useRef(null)
    const isFirstRender = useRef(true)

    // on sheet load, we are not sorting or filtering but may scroll
    const isSortingOrFiltering = useRef(false)

    // useEffect to query for Big Sheets' data.
    useEffect(() => {
        const handleScroll = () => {
            const { scrollHeight, scrollTop, clientHeight } = tableContainerRef.current

            if (scrollHeight - scrollTop - clientHeight < 10000) {
                fetchData()
            }
        }

        const fetchData = async () => {
            // Skip initial fetch if it's the first render
            if (isFirstRender.current) {
                isFirstRender.current = false
                return
            }
            const dataMissing = rowCount - rowsProcessed.length
            // Do not query if there is no missing data (e.g. already queried)
            // Do not query if the query is in progress
            // Small sheets do not need to request data

            // if you scroll before sort/filter, isSortingOrFiltering is false
            // making all the conditions false. Therefore we load the next 1000 rows

            // if you apply a filter or sort, isSortingOrFiltering is true
            // this will prevent loading the next 1000 rows on sort/filter by returning early
            if (dataMissing <= 0 || query.current || canFitInMemory || isSortingOrFiltering.current) {
                // reset isSortingOrFiltering to false so that we can keep scrolling after sort or filter is applied
                isSortingOrFiltering.current = false
                return
            }
            query.current = loadNextInterval()
            // after loading the next 1000 rows, reset isSortingOrFiltering to false
            // so that we can keep scrolling even after sort or filter is applied mid-scroll
            isSortingOrFiltering.current = false
            return query.current
        }

        // get data, and log any errors to client and backend
        fetchData()
            .then(() => (query.current = null))
            .catch((error) => {
                handleErr(error)
                BACKENDERROR(error)
            })

        const tableContainer = tableContainerRef.current
        if (tableContainer) {
            tableContainer.addEventListener("scroll", handleScroll)
        }

        return () => {
            if (tableContainer) {
                tableContainer.removeEventListener("scroll", handleScroll)
            }
            // Cleanup any queries on unmount
            query?.current?.abort?.()
        }
    }, [canFitInMemory, id, loadNextInterval, rowCount, rowsProcessed.length, sheetId])

    // Dictionary of column ID <--> column index pairs for easy lookup since we don't have access to individual columns
    const columnIdToIndex = {}
    // Indexing in the mantine sheet is off by 1
    // Exclude the index column from the columnIdToIndex mapping
    columnsProcessed
        ?.filter((column) => column.id !== sheetConstants.REMOVE_ROW_COLUMN_INDEX)
        .forEach((column, columnIndex) => (columnIdToIndex[column.id] = columnIndex - 1))

    const onColumnFiltersChange = (columnFilters) => {
        // if any filters are active, we need to make sure the math bar is always hidden
        if (columnFilters.length) {
            shouldDisplayMathBar && toggleShowMath(createSheetKey(sheetId), false)
        }

        // if we're here, it means we're filtering
        // set flag to true
        isSortingOrFiltering.current = true
        columnsProcessed
            .filter(
                (column) =>
                    column.id !== sheetConstants.REMOVE_ROW_COLUMN_INDEX &&
                    !columnFilters.some((filter) => filter.id === column.id),
            )
            .forEach((column) => clearFilter(id, columnIdToIndex[column.id]))
        columnFilters.forEach((filter) => {
            const columnIndex = columnIdToIndex[filter?.id]
            const column = columnsProcessed.find((col) => col.id === Number(filter?.id))
            // make any data-type specific adjustments to filter values for download / other
            // app-state purposes
            const value = coerceValueToDataType(filter?.value, column)
            searchByColumnAtIndex(id, columnIndex, value)
        })
    }

    const onSortingChange = (sorting) => {
        // if we're here, it means we're filtering
        // set flag to true
        isSortingOrFiltering.current = true

        // Find currently pinned column
        const currentlyPinnedColumnId = Object.keys(pinnedColumns).find((id) => pinnedColumns[id])

        if (sorting.length === 0) {
            // No sorting applied - this means user cleared the sort
            // If there's a pinned column, unpin it since the user cleared the sort
            if (currentlyPinnedColumnId) {
                if (!publicSheet) {
                    toggleDefaultSortOnColumn(id, Number(currentlyPinnedColumnId), 0)
                }
            }
            return
        }

        const sortedColumnId = sorting[0]?.id
        const sortType = sorting[0]?.desc
            ? DjangIO.app.userdata.spreadsheet.types.SheetSortType.descending.value
            : DjangIO.app.userdata.spreadsheet.types.SheetSortType.ascending.value
        const columnIndex = columnIdToIndex[sortedColumnId]

        // If the sorted column is currently pinned, unpin it
        // If a different column is pinned, unpin the currently pinned column
        if (currentlyPinnedColumnId) {
            if (currentlyPinnedColumnId === sortedColumnId) {
                // User is sorting a pinned column, so unpin it
                if (!publicSheet) {
                    toggleDefaultSortOnColumn(id, Number(currentlyPinnedColumnId), 0)
                }
            } else {
                // User is sorting a different column, so unpin the currently pinned column
                if (!publicSheet) {
                    toggleDefaultSortOnColumn(id, Number(currentlyPinnedColumnId), 0)
                }
            }
        }

        sortByColumnAtIndex(id, columnIndex, sortType)
    }

    return (
        <MantineReactTableWrapper
            id={id}
            data={rowsProcessed}
            columns={columnsProcessed}
            enableColumnDragging
            enableColumnOrdering
            enableColumnResizing={isResizable}
            enableDensityToggle={false}
            enableStickyHeader
            enableStickyFooter={shouldDisplayMathBar}
            onColumnSizingChange={handleColumnSizeChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnFiltersChange={onColumnFiltersChange}
            onSortingChange={onSortingChange}
            enableGrouping={true}
            renderTopToolbarCustomActions={renderTopToolbarCustomActions}
            rowCount={rowCount}
            enableFullScreen={false}
            isLoading={isLoadingBigSheetData}
            tableContainerRef={tableContainerRef}
            canFitInMemory={canFitInMemory}
            enableGlobalFilter={!!canFitInMemory}
            initialSorting={initialSorting}
        />
    )
}

LocalMantineSheet.defaultProps = {
    canRenameColumns: true,
}

export const MantineSheet = connect((state, props) => mantineSheetConnection(state, props), {
    sendUserAction,
    updateUserdata,
    renameColumn,
    searchByColumnAtIndex,
    sortByColumnAtIndex,
    clearFilter,
    toggleDefaultSortOnColumn,
})(LocalMantineSheet)
